import type { IRainAgentConfig } from '../hooks/scope';
import {
  EMessageDirection,
  EMessageType,
  type IChatMessage,
} from '@kognitos/lattice';

const OPEN = '<user-message>';
const CLOSE = '</user-message>';

/**
 * Extracts user-facing blocks from one accumulated assistant text message.
 *
 * A missing closing tag ends at this message's boundary. Rain splits text at
 * tool calls, so an open block never carries into subsequent tool activity.
 */
export function readUserMessages(content: string): {
  text: string;
  incomplete: boolean;
} {
  const blocks: string[] = [];
  let cursor = 0;
  let incomplete = false;
  while (cursor < content.length) {
    const start = content.indexOf(OPEN, cursor);
    if (start < 0) break;
    const end = content.indexOf(CLOSE, start + OPEN.length);
    if (end < 0) {
      incomplete = true;
      let body = content.slice(start + OPEN.length);
      // Hide a closing tag arriving across streaming deltas.
      for (let length = CLOSE.length - 1; length > 0; length--) {
        if (body.endsWith(CLOSE.slice(0, length))) {
          body = body.slice(0, -length);
          break;
        }
      }
      if (!body.includes(OPEN) && body.trim()) blocks.push(body.trim());
      break;
    }
    const body = content.slice(start + OPEN.length, end);
    if (body.includes(OPEN)) incomplete = true;
    else if (body.trim()) blocks.push(body.trim());
    cursor = end + CLOSE.length;
  }
  return { text: blocks.join('\n\n'), incomplete };
}

/** Identifies assistant prose without touching forms, approvals, failures or user messages. */
export function isAssistantText(message: IChatMessage): boolean {
  return (
    message.direction === EMessageDirection.INCOMING &&
    !message.widget &&
    (message.type === undefined || message.type === EMessageType.DEFAULT) &&
    Boolean(message.content)
  );
}

/** Resolves the opt-in filter; unknown pins hide untagged prose until metadata arrives. */
export function shouldFilterUserMessages(
  setting: IRainAgentConfig['filterUserMessages'],
  agentVersion?: string,
): boolean {
  if (typeof setting !== 'object') return setting === true;
  return (
    agentVersion === undefined || Number(agentVersion) >= setting.minVersion
  );
}
